'use client';

import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';

import { getConfig } from '@zoomit/configs';

import { OriginURL } from '../constants/apis';
import { useWebKitCustomization } from '../hooks/useWebKitCustomization';

export type OriginType = {
  baseUrl: string;
  originUrl: string;
  baseMediaUrl: string;
  baseApiUrl: string;
  isSameOrigin: boolean;
};

export const OriginContext = createContext<OriginType>({
  baseApiUrl: getConfig('API_BASE_URL'),
  baseMediaUrl: `${getConfig('API_BASE_URL')}/media`,
  originUrl: getConfig('ORIGIN_URL'),
  baseUrl: ``,
  isSameOrigin: true,
});

type Props = {
  children: ReactNode;
  origin?: string | 'Catalog';
};

export const OriginProvider = (props: Props) => {
  const { children, origin } = props;

  const externalOrigins = useWebKitCustomization((x) => x.externalOrigin);
  const catalogOrigin = useWebKitCustomization((x) => x.catalogProvider);

  switch (origin) {
    case 'Catalog': {
      const { baseApiUrl, baseMediaUrl, baseUrl, originUrl } = catalogOrigin;
      const isSameOrigin = originUrl === OriginURL;
      const catalogOriginSpecs = {
        baseApiUrl: baseApiUrl,
        baseMediaUrl: baseMediaUrl,
        originUrl: originUrl,
        baseUrl: isSameOrigin ? '' : baseUrl,
        isSameOrigin,
      };

      return <OriginContext.Provider value={catalogOriginSpecs}>{children}</OriginContext.Provider>;
    }

    default: {
      const externalOrigin = origin ? externalOrigins?.[origin] : undefined;
      const originUrl = externalOrigin?.baseUrl || getConfig('ORIGIN_URL');

      const isSameOrigin = originUrl === OriginURL;

      const externalOriginSpecs: OriginType = {
        baseApiUrl: externalOrigin?.baseApiUrl || getConfig('API_BASE_URL'),
        baseMediaUrl: externalOrigin?.baseMediaUrl || `${getConfig('API_BASE_URL')}/media`,
        isSameOrigin: originUrl === OriginURL,
        originUrl: originUrl,
        baseUrl: isSameOrigin ? '' : originUrl,
      };
      return <OriginContext.Provider value={externalOriginSpecs}>{children}</OriginContext.Provider>;
    }
  }
};

export const useOrigin = () => {
  return useContext(OriginContext);
};
